import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getEventName, getUserActivePlan } from "@helpers/profileHelper.js";
import { profileTypeEndPoint, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum.js";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import styles from "./CompanyOverview.module.scss";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { cachingPanelData, companyCategoryData, companyEvents } from "@containers/CompanyProfile/utils/enum.js";
import axios from "axios";
import { buyerCachingHandler, companyCategoryHandler, profileMapDataHandler, supplierCachingHandler } from "@redux/CompanyProfile/companyProfileSlice";
import Button from "@components/base/button";
import { CLOUDINARY_INTEL_BASE_URL } from "@utils/enum";
import NoSearchDataComponent from "@shared/country-components/no-search-data";
import TradeOverview from "@components/CompanyProfileTradeOverview/TradeOverview";

const CompanyOverview = ({ shipmentDistributionData, currentKey = "trade_country", shipmentDistributionLoader, handlePanelClick }) => {
  const { companyID, companyname } = useParams();
  const [error, setError] = useState(false);
  const [profileData, setProfileData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(currentKey);
  const shipmentDistribution = [];
  const [payload, setPayload] = useState({
    slug: companyname,
    isSubsidiaryIncluded: true,
  });
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const mergedID = new URLSearchParams(searchUrlParams).get("mergedID");
  const isMerged = new URLSearchParams(searchUrlParams).get("isMerged");
  const country = new URLSearchParams(searchUrlParams).get("country");
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [loader, setLoader] = useState(true);
  const dispatch = useDispatch();
  const {
    companyCategory,
    isParentSubs,
    countDetails,
    buyerCaching,
    supplierCaching,
    tabDetails: { activePanelId, activeTabId },
  } = useSelector((state) => state.companyProfile);
  const source = useRef(null);
  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";
  const redirectTab = new URLSearchParams(searchUrlParams).get("redirectTab");

  useEffect(() => {
    setActiveCategory(currentKey);
  }, [currentKey]);

  useLayoutEffect(() => {
    if (activePanelId && activeTabId) {
      const requestPayload = {
        profile_type: profileTypeEnum.company,
        _id: companyID,
        category: activePanelId,
        sub_category: "overview",
        map_type: activeCategory,
        slug: companyname,
        isSubsidiaryIncluded: isParentSubs,
      };

      if (mergedID) {
        requestPayload.mergedID = +mergedID;
        requestPayload.isMerged = true;
      }
      if (source.current) {
        source.current.cancel("");
      }
      const CancelToken = axios.CancelToken;
      source.current = CancelToken.source();
      setLoader(true);
      dispatch(companyCategoryHandler(activePanelId));
      if (cachingPanelData.importers === activePanelId && buyerCaching[activeCategory]) {
        dispatch(companyCategoryHandler(activePanelId));
        dispatch(profileMapDataHandler(buyerCaching?.profileDetails));
        setLoader(false);
      } else if (cachingPanelData.exporters === activePanelId && supplierCaching[activeCategory]) {
        dispatch(companyCategoryHandler(activePanelId));
        dispatch(profileMapDataHandler(supplierCaching?.profileDetails));
        setLoader(false);
      } else {
        setError(false);
        if (!companyCategory || companyCategory !== activePanelId) {
          dispatch(
            profileMapDataHandler({
              loading: true,
            })
          );
        }
        axios
          .post(profileTypeEndPoint.ports.getMapData, requestPayload, {
            cancelToken: source.current.token,
          })
          .then((res) => {
            const { data, status } = res.data;
            if (status && status === "success") {
              let profileObj = {};
              setProfileData(res.data.data);

              if (mergedID) {
                localStorage.setItem("mergeCompany", JSON.stringify({ merged_company_ids: data.merged_company_ids, merged_company_names: data.merged_company_names }));
              }
              const { trading_partner_count, hs_code_count, reverse_relation, nvocc_count, vocc_count, vessel_count, freight_forwarder_count, company_id, slug } = data;
              if (!companyCategory || companyCategory !== activePanelId || (companyCategory === activePanelId && activeCategory === companyCategoryData[0].id)) {
                profileObj = {
                  ...data,
                  nvoccCount: nvocc_count ?? 0,
                  voccCount: vocc_count ?? 0,
                  vesselCount: vessel_count ?? 0,
                  hsCodeCount: hs_code_count ?? 0,
                  tradingPartnerCount: trading_partner_count ?? 0,
                  freightForwarderCount: freight_forwarder_count ?? 0,
                  reverseRelation: reverse_relation ?? true,
                  loading: false,
                  companyId: company_id ?? "",
                  companyName: slug ?? "",
                };
                dispatch(companyCategoryHandler(activePanelId));
                if (activeCategory === companyCategoryData[0].id) {
                  dispatch(profileMapDataHandler(profileObj));
                }
              }
              if (cachingPanelData.importers === activePanelId) {
                let buyerCachingClone = JSON.parse(JSON.stringify(buyerCaching));
                if (activeCategory === companyCategoryData[0].id) {
                  Object.keys(buyerCachingClone.buyerCounts).forEach((key) => {
                    buyerCachingClone.buyerCounts[key] = res.data.data[key];
                  });
                }
                buyerCachingClone[activeCategory] = JSON.parse(JSON.stringify(res.data.data[activeCategory]));
                buyerCachingClone[`${activeCategory}_must_not`] = JSON.parse(JSON.stringify(res.data.data.must_not));
                if (Object.keys(profileObj)?.length) {
                  buyerCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
                }
                dispatch(buyerCachingHandler(buyerCachingClone));
                setLoader(false);
              } else if (cachingPanelData.exporters === activePanelId) {
                let supplierCachingClone = JSON.parse(JSON.stringify(supplierCaching));
                if (activeCategory === companyCategoryData[0].id) {
                  Object.keys(supplierCachingClone.supplierCounts).forEach((key) => {
                    supplierCachingClone.supplierCounts[key] = res.data.data[key];
                  });
                }
                supplierCachingClone[activeCategory] = JSON.parse(JSON.stringify(res.data.data[activeCategory]));
                supplierCachingClone[`${activeCategory}_must_not`] = JSON.parse(JSON.stringify(res.data.data.must_not));
                if (Object.keys(profileObj)?.length) {
                  supplierCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
                }
                dispatch(supplierCachingHandler(supplierCachingClone, profileData));
                setLoader(false);
              }
              if (redirectTab === "contacts") handlePanelClick("contact_details", "contactDetails");
            }
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              Sentry.captureException(error);
              if (!STARTERPLAN) setError(true);
              dispatch(
                profileMapDataHandler({
                  loading: false,
                })
              );

              if (!STARTERPLAN) {
                toast.error("Something went wrong. Please try again later !!!");
              }
            }
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, isParentSubs, activePanelId]);

  useEffect(() => {
    let payloadClone = JSON.parse(JSON.stringify(payload));
    payloadClone.isSubsidiaryIncluded = isParentSubs;
    if (mergedID) {
      payloadClone.isMerged = isMerged === "false" ? false : true;
      payloadClone.mergedID = +mergedID;
    }
    setPayload(payloadClone);
  }, [activePanelId, activeTabId, isParentSubs]);

  const handleCategoryClick = (category) => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, companyEvents.overview[category?.eventEnum]), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    setActiveCategory(category.id);
  };

  const checkEmptyProfileData = () => {
    let data = shipmentDistribution && shipmentDistribution.filter((companyData) => companyData?.doc_count > 0);
    return data.length > 0;
  };
  const handleGetInTouchSubmit = () => {
    const { user_active_plan } = creditDetails.data?.[0] ?? "";
    const planId = user_active_plan;
    const params = {
      planId,
      countries: country,
      key: "company_profile",
    };
    axios
      .get(profileTypeEndPoint.getInTouch, { params })
      .then((res) => {
        if (res?.data?.status === "success") {
          toast.success(res.data.message);
          setButtonClicked(true);
        } else {
          toast.error(res.data.message ? res.data.message : "Something went wrong. Please try again later !!!");
        }
      })
      .catch(() => {
        toast.error("Something went wrong. Please try again later !!!");
      });
  };

  return (
    <>
      {error ? (
        <div className={styles.noDataContainer}>
          <NoSearchDataComponent content={"Unable to fetch the data at current moment."} />
        </div>
      ) : shipmentDistributionData.length === 0 && countDetails?.is_parent_company && !shipmentDistributionLoader && !countDetails?.loading ? (
        <div className="label-md text-center no-shipment-data">
          <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1686817425/intel/icons/brightness_alert_lwi77k.svg" />
          <p>We can't find any shipments for this company. There may be shipments being imported by its subsidiaries. Please switch on the "Include Subsidiaries Data" option above.</p>
        </div>
      ) : (
        <div className={`${shipmentDistribution.length === 0 || checkEmptyProfileData() ? "" : "tw-hidden"} ${loader ? "tw-pl-3" : ""}`}>
          <TradeOverview loader={loader} dataSource={dataSource} pathname={pathname} userActivePlan={getUserActivePlan()} setLoader={setLoader} countDetails={countDetails} activePanelId={activePanelId} activeTabId={activeTabId} categoryData={companyCategoryData} profileData={profileData.length > 0 && profileData[activeCategory]?.length > 0 ? profileData : activePanelId === "importers" && buyerCaching[activeCategory]?.length > 0 ? buyerCaching : activePanelId === "exporters" && supplierCaching[activeCategory]?.length > 0 ? supplierCaching : []} setProfileData={setProfileData} activeCategory={activeCategory} handleCategoryClick={handleCategoryClick} />
        </div>
      )}
      {!(shipmentDistribution.length === 0 || checkEmptyProfileData()) && countDetails.loading === false && (
        <div className={`${styles.get_in_touch} mx-auto my-4 card d-flex flex-column justify-content-center`}>
          <div className="label-md text-center">
            No data found for your subscribed plan. We have found {shipmentDistribution?.[0]?.doc_count ?? ""} shipments in historical data.
            <div className={`${styles.header} d-flex justify-content-center align-items-center my-3`}>
              <div className="d-flex justify-content-center align-items-center">
                <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg`} />
              </div>
            </div>
          </div>
          <div className="px-2 text-center label-md">You do not have access to the shipments beyond your subscribed plan. Get in touch with your account manager for more data.</div>
          <div className="my-2 mx-auto">
            <Button id="email-alert" className="mx-2" variant={"primaryWithIcon"} size={"large"} disabled={buttonClicked} onClick={handleGetInTouchSubmit}>
              Get in touch
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyOverview;
